import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import {
  AlertCircle,
  Check,
  ChevronLeft,
  Loader2,
  Menu,
  Palette,
  Save,
  Settings2,
  Sparkles,
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  getStarterTemplateByVertical,
  getStarterTemplatePage,
  SITE_THEME_BUTTON_STYLES,
  SITE_THEME_CARD_STYLES,
  SITE_THEME_PRESETS,
  normalizeSiteTheme,
  sanitizeSiteTheme,
  type BrandColors,
  type BrandFonts,
  type SiteTheme,
} from "@ease/page-builder";
import { apiFetch, ApiError } from "@/lib/api-client";
import { BRAND_CONTRACT_PATH } from "@/lib/api-paths";
import {
  FONT_MOOD_OPTIONS,
  buildPreviewTheme as buildThemePreviewFromBrand,
  buildQuickSiteTheme,
  detectFontMood,
  resolveBrandInheritance,
  summarizeThemeInheritance,
  type FontMoodId,
  type ThemeInheritanceMap,
} from "@/hooks/use-site-theme";

interface SiteRecord {
  id: string;
  name: string;
  slug: string;
  homePageId: string | null;
  globalTheme: SiteTheme;
  headScripts: string | null;
  bodyEndScripts: string | null;
  seoDefaults: Record<string, unknown>;
  headerContent: Record<string, unknown> | null;
  footerContent: Record<string, unknown> | null;
  robotsTxt: string | null;
}

interface BrandKitSummary {
  logoUrl: string | null;
  businessName: string | null;
  industry: string | null;
  colors: BrandColors;
  fonts: BrandFonts;
  brandVoice: string | null;
  tagline: string | null;
}

interface PickOneFormState {
  siteName: string;
  businessName: string;
  tagline: string;
  logoUrl: string;
  fontMood: FontMoodId;
  primaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
}

interface PageOption {
  id: string;
  title: string;
  slug: string;
}

type NavType = "main" | "footer" | "mobile";

interface NavItemDraft {
  id: string;
  label: string;
  url: string;
  openInNewTab: boolean;
}

interface SiteNavigationResponse {
  id: string;
  siteId: string;
  navType: NavType;
  items: Array<{
    id: string;
    label: string;
    url?: string;
    openInNewTab?: boolean;
  }>;
  updatedAt: string;
}

interface SiteFormState {
  name: string;
  slug: string;
  metaDescription: string;
  homePageId: string;
  themePresetId: string;
  themeBrand: string;
  themeBrandDark: string;
  themeSecondary: string;
  themeAccent: string;
  themeBackground: string;
  themeSurface: string;
  themeMuted: string;
  themeText: string;
  themeHeadingFont: string;
  themeBodyFont: string;
  themeBorderRadius: string;
  themeSpacing: string;
  themeButtonStyle: string;
  themeCardStyle: string;
  headScripts: string;
  bodyEndScripts: string;
  robotsTxt: string;
}

const INITIAL_FORM: SiteFormState = {
  name: "",
  slug: "",
  metaDescription: "",
  homePageId: "",
  themePresetId: "",
  themeBrand: "",
  themeBrandDark: "",
  themeSecondary: "",
  themeAccent: "",
  themeBackground: "",
  themeSurface: "",
  themeMuted: "",
  themeText: "",
  themeHeadingFont: "",
  themeBodyFont: "",
  themeBorderRadius: "",
  themeSpacing: "",
  themeButtonStyle: "",
  themeCardStyle: "",
  headScripts: "",
  bodyEndScripts: "",
  robotsTxt: "",
};

const UNSUPPORTED_SURFACES = [
  "Favicon asset management from site settings",
];

const NAV_TYPES: NavType[] = ["main", "footer", "mobile"];

const COMMON_FONT_OPTIONS = [
  "Inter",
  "DM Sans",
  "Playfair Display",
  "Source Sans 3",
  "Montserrat",
  "Poppins",
  "Manrope",
];

const DEFAULT_THEME_PRESET = SITE_THEME_PRESETS.find((preset) => preset.id === "modern")?.theme
  ?? SITE_THEME_PRESETS[0]?.theme
  ?? {};
const DEFAULT_PREVIEW_THEME = normalizeSiteTheme(DEFAULT_THEME_PRESET);
const DEFAULT_PREVIEW_COLORS = DEFAULT_PREVIEW_THEME.colors ?? {};
const DEFAULT_PREVIEW_BRAND = DEFAULT_PREVIEW_COLORS.brand ?? "#1040CC";
const DEFAULT_PREVIEW_BRAND_DARK = DEFAULT_PREVIEW_COLORS.brandDark ?? DEFAULT_PREVIEW_BRAND;
const DEFAULT_PREVIEW_SECONDARY = DEFAULT_PREVIEW_COLORS.secondary ?? DEFAULT_PREVIEW_BRAND;
const DEFAULT_PREVIEW_ACCENT = DEFAULT_PREVIEW_COLORS.accent ?? "#F05E0A";
const DEFAULT_PREVIEW_BACKGROUND = DEFAULT_PREVIEW_COLORS.background ?? "#FFFFFF";
const DEFAULT_PREVIEW_SURFACE = DEFAULT_PREVIEW_COLORS.surface ?? "#F8FAFC";
const DEFAULT_PREVIEW_MUTED = DEFAULT_PREVIEW_COLORS.muted ?? "#E2E8F0";
const DEFAULT_PREVIEW_TEXT = DEFAULT_PREVIEW_COLORS.text ?? "#0F172A";
const DEFAULT_PREVIEW_RADIUS = DEFAULT_PREVIEW_THEME.borderRadius ?? "12px";
const DEFAULT_PREVIEW_SPACING = DEFAULT_PREVIEW_THEME.spacing ?? "4rem";
const FOCUS_RING_CLASS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const RESET_CHIP_CLASS = `inline-flex min-h-8 items-center justify-center rounded-full border border-border bg-background px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground transition hover:bg-accent hover:text-foreground ${FOCUS_RING_CLASS}`;
const ADVISORY_NOTICE_CLASS = "flex items-start gap-3 rounded-2xl border border-accent/40 bg-accent/10 px-4 py-3 text-sm text-accent-foreground";
const SUCCESS_NOTICE_CLASS = "rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary";
const ADVISORY_SECTION_CLASS = "rounded-[24px] border border-accent/40 bg-accent/10 p-6 shadow-sm";
const ADVISORY_ITEM_CLASS = "rounded-2xl border border-accent/30 bg-background/80 px-4 py-3 text-sm text-foreground";

export default function SiteSettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [brand, setBrand] = useState<BrandKitSummary | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [form, setForm] = useState<SiteFormState>(INITIAL_FORM);
  const [pickOneForm, setPickOneForm] = useState<PickOneFormState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pageLoadError, setPageLoadError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [activeNavType, setActiveNavType] = useState<NavType>("main");
  const [navItems, setNavItems] = useState<NavItemDraft[]>([]);
  const [isLoadingNavigation, setIsLoadingNavigation] = useState(false);
  const [isSavingNavigation, setIsSavingNavigation] = useState(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const [navigationMessage, setNavigationMessage] = useState<string | null>(null);
  const isPickOneEntry = searchParams.get("entry") === "pick-one";
  const activePanel = searchParams.get("panel");
  const templateVertical = searchParams.get("template");
  const starterPageId = searchParams.get("starter") ?? undefined;

  const selectedSite = useMemo(
    () => sites.find((site) => site.id === selectedSiteId) ?? null,
    [selectedSiteId, sites],
  );
  const selectedTemplate = useMemo(
    () => (templateVertical ? getStarterTemplateByVertical(templateVertical) : undefined),
    [templateVertical],
  );
  const selectedStarter = useMemo(
    () => (templateVertical ? getStarterTemplatePage(templateVertical, starterPageId) : undefined),
    [starterPageId, templateVertical],
  );

  useEffect(() => {
    let isCancelled = false;

    async function load() {
      setIsLoading(true);
      setLoadError(null);
      setPageLoadError(null);

      try {
        const [sitesResult, pagesResult, brandResult] = await Promise.allSettled([
          apiFetch<{ data: SiteRecord[] }>("/crm/pages/sites"),
          apiFetch<{ data: { items: PageOption[] } }>("/crm/pages?limit=100"),
          apiFetch<{ data: BrandKitSummary | null }>(BRAND_CONTRACT_PATH).catch(() => ({ data: null })),
        ]);

        if (isCancelled) return;

        if (sitesResult.status === "rejected") {
          throw sitesResult.reason;
        }

        const loadedSites = sitesResult.value.data ?? [];
        const loadedPages = pagesResult.status === "fulfilled"
          ? (pagesResult.value.data.items ?? [])
          : [];
        const requestedSiteId = searchParams.get("siteId");
        const preferredSiteId = requestedSiteId && loadedSites.some((site) => site.id === requestedSiteId)
          ? requestedSiteId
          : loadedSites[0]?.id ?? "";

        setSites(loadedSites);
        setPages(loadedPages);
        setBrand(brandResult.status === "fulfilled" ? (brandResult.value.data ?? null) : null);
        setSelectedSiteId(preferredSiteId);

        if (pagesResult.status === "rejected") {
          setPageLoadError(resolveUiErrorMessage(
            pagesResult.reason,
            "Page inventory is temporarily unavailable. You can still edit site-level settings, but home-page assignment is disabled until `/crm/pages` recovers.",
            "You appear to be offline. Reconnect to reload the page inventory and home-page assignment options.",
          ));
        }
      } catch (error) {
        if (isCancelled) return;
        setLoadError(resolveUiErrorMessage(
          error,
          "Failed to load site settings.",
          "You appear to be offline. Reconnect and try loading site settings again.",
        ));
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      isCancelled = true;
    };
  }, [searchParams]);

  useEffect(() => {
    if (!selectedSite) {
      setForm(INITIAL_FORM);
      setNavItems([]);
      return;
    }

    const theme = sanitizeSiteTheme(selectedSite.globalTheme);
    setForm({
      name: selectedSite.name,
      slug: selectedSite.slug,
      metaDescription: readMetaDescription(selectedSite.seoDefaults),
      homePageId: selectedSite.homePageId ?? "",
      themePresetId: theme.presetId ?? "",
      themeBrand: theme.colors?.brand ?? "",
      themeBrandDark: theme.colors?.brandDark ?? "",
      themeSecondary: theme.colors?.secondary ?? "",
      themeAccent: theme.colors?.accent ?? "",
      themeBackground: theme.colors?.background ?? "",
      themeSurface: theme.colors?.surface ?? "",
      themeMuted: theme.colors?.muted ?? "",
      themeText: theme.colors?.text ?? "",
      themeHeadingFont: theme.fonts?.heading?.family ?? "",
      themeBodyFont: theme.fonts?.body?.family ?? "",
      themeBorderRadius: theme.borderRadius ?? "",
      themeSpacing: theme.spacing ?? "",
      themeButtonStyle: theme.buttonStyle ?? "",
      themeCardStyle: theme.cardStyle ?? "",
      headScripts: selectedSite.headScripts ?? "",
      bodyEndScripts: selectedSite.bodyEndScripts ?? "",
      robotsTxt: selectedSite.robotsTxt ?? "",
    });
    setSaveMessage(null);
  }, [selectedSite]);

  useEffect(() => {
    if (!selectedSite || activePanel !== "navigation") {
      return;
    }

    let isCancelled = false;

    async function loadNavigation() {
      setIsLoadingNavigation(true);
      setNavigationError(null);
      setNavigationMessage(null);

      try {
        const response = await apiFetch<{ data?: SiteNavigationResponse }>(
          `/crm/pages/sites/${selectedSite!.id}/nav/${activeNavType}`,
        );
        if (isCancelled) return;

        setNavItems((response.data?.items ?? []).map((item) => ({
          id: item.id,
          label: item.label,
          url: item.url ?? "",
          openInNewTab: item.openInNewTab ?? false,
        })));
      } catch (error) {
        if (isCancelled) return;
        setNavItems([]);
        setNavigationError(resolveUiErrorMessage(
          error,
          "Failed to load site navigation.",
          "You appear to be offline. Reconnect before loading site navigation.",
        ));
      } finally {
        if (!isCancelled) {
          setIsLoadingNavigation(false);
        }
      }
    }

    void loadNavigation();

    return () => {
      isCancelled = true;
    };
  }, [activeNavType, activePanel, selectedSite]);

  useEffect(() => {
    if (!isPickOneEntry || !selectedSite) {
      setPickOneForm(null);
      return;
    }

    const siteTheme = buildThemePreviewFromBrand(normalizeSiteTheme(selectedSite.globalTheme), brand);
    setPickOneForm({
      siteName: selectedSite.name,
      businessName: brand?.businessName ?? selectedSite.name,
      tagline: brand?.tagline ?? "",
      logoUrl: brand?.logoUrl ?? "",
      fontMood: detectFontMood(siteTheme),
      primaryColor: siteTheme.colors?.brand ?? brand?.colors.primary ?? DEFAULT_PREVIEW_BRAND,
      accentColor: siteTheme.colors?.accent ?? brand?.colors.accent ?? DEFAULT_PREVIEW_ACCENT,
      backgroundColor: siteTheme.colors?.background ?? brand?.colors.background ?? DEFAULT_PREVIEW_BACKGROUND,
      textColor: siteTheme.colors?.text ?? brand?.colors.text ?? DEFAULT_PREVIEW_TEXT,
    });
    setSaveMessage(null);
  }, [brand, isPickOneEntry, selectedSite]);

  const fontOptions = useMemo(() => {
    const values = new Set<string>(COMMON_FONT_OPTIONS);
    for (const preset of SITE_THEME_PRESETS) {
      const heading = preset.theme.fonts?.heading?.family;
      const body = preset.theme.fonts?.body?.family;
      if (heading) values.add(heading);
      if (body) values.add(body);
    }
    if (brand?.fonts.headings?.family) values.add(brand.fonts.headings.family);
    if (brand?.fonts.body?.family) values.add(brand.fonts.body.family);
    return [...values];
  }, [brand]);

  const draftTheme = useMemo(() => buildThemePayload(form), [form]);
  const resolvedTheme = useMemo(
    () => resolveBrandInheritance(draftTheme, brand),
    [brand, draftTheme],
  );
  const inheritanceSummary = useMemo(
    () => summarizeThemeInheritance(resolvedTheme.inheritance),
    [resolvedTheme.inheritance],
  );
  const hasBrandRefreshAction = Boolean(brand) && inheritanceSummary.siteOverrideCount > 0;
  const previewTheme = resolvedTheme.theme;
  const previewStyle = useMemo(() => buildPreviewStyle(previewTheme), [previewTheme]);
  const pickOneTheme = useMemo(() => {
    if (!pickOneForm) {
      return normalizeSiteTheme({});
    }

    return buildQuickSiteTheme({
      ...(templateVertical ? { templateVertical } : { fontMoodId: pickOneForm.fontMood }),
      brand: pickOneForm.primaryColor,
      accent: pickOneForm.accentColor,
      background: pickOneForm.backgroundColor,
      text: pickOneForm.textColor,
    });
  }, [pickOneForm, templateVertical]);
  const pickOnePreviewTheme = useMemo(
    () => buildThemePreviewFromBrand(pickOneTheme, brand),
    [brand, pickOneTheme],
  );
  const pickOnePreviewStyle = useMemo(
    () => buildPreviewStyle(pickOnePreviewTheme),
    [pickOnePreviewTheme],
  );

  const pickOneBrandSeed = useMemo(() => buildBrandSeedFromTheme(pickOneTheme), [pickOneTheme]);

  const persistPickOneCustomization = async () => {
    if (!selectedSite || !pickOneForm) {
      return false;
    }

    setIsSaving(true);
    setLoadError(null);
    setSaveMessage(null);

    try {
      const [updatedSiteResponse] = await Promise.all([
        apiFetch<{ data: SiteRecord }>(`/crm/pages/sites/${selectedSite.id}`, {
          method: "PUT",
          body: JSON.stringify({
            name: pickOneForm.siteName.trim(),
            globalTheme: {
              ...(pickOneTheme.presetId ? { presetId: pickOneTheme.presetId } : {}),
            },
          }),
        }),
        apiFetch(BRAND_CONTRACT_PATH, {
          method: "PUT",
          body: JSON.stringify({
            businessName: pickOneForm.businessName.trim() || null,
            tagline: normalizeNullableText(pickOneForm.tagline),
            logoUrl: normalizeNullableText(pickOneForm.logoUrl),
            colors: pickOneBrandSeed.colors,
            fonts: pickOneBrandSeed.fonts,
            ...(templateVertical ? { industry: templateVertical } : {}),
          }),
        }),
      ]);

      setSites((current) => current.map((site) => (
        site.id === updatedSiteResponse.data.id ? updatedSiteResponse.data : site
      )));
      setBrand((current) => ({
        logoUrl: normalizeNullableText(pickOneForm.logoUrl),
        businessName: pickOneForm.businessName.trim() || null,
        industry: templateVertical ?? current?.industry ?? null,
        colors: pickOneBrandSeed.colors,
        fonts: pickOneBrandSeed.fonts,
        brandVoice: current?.brandVoice ?? null,
        tagline: normalizeNullableText(pickOneForm.tagline),
      }));
      setSaveMessage("Brand defaults saved. This site keeps inheriting them until you add explicit site-level overrides.");
      return true;
    } catch (error) {
      setLoadError(resolveUiErrorMessage(
        error,
        "Failed to save Pick One customization.",
        "You appear to be offline. Reconnect before saving Pick One customization.",
      ));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedSite) return;

    setIsSaving(true);
    setLoadError(null);
    setSaveMessage(null);

    try {
      const nextSeoDefaults = {
        ...selectedSite.seoDefaults,
        ...(form.metaDescription.trim() ? { description: form.metaDescription.trim() } : {}),
      };

      if (!form.metaDescription.trim()) {
        delete nextSeoDefaults.description;
      }

      const response = await apiFetch<{ data: SiteRecord }>(`/crm/pages/sites/${selectedSite.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: form.name.trim(),
          slug: form.slug.trim(),
          globalTheme: {
            ...draftTheme,
            ...(selectedSite.globalTheme.customCss ? { customCss: selectedSite.globalTheme.customCss } : {}),
          },
          seoDefaults: nextSeoDefaults,
          homePageId: form.homePageId || null,
          headScripts: normalizeNullableText(form.headScripts),
          bodyEndScripts: normalizeNullableText(form.bodyEndScripts),
          robotsTxt: normalizeNullableText(form.robotsTxt),
        }),
      });

      const updatedSite = response.data;
      setSites((current) => current.map((site) => (site.id === updatedSite.id ? updatedSite : site)));
      setSaveMessage("Site theme and settings saved to the mounted site resource.");
    } catch (error) {
      setLoadError(resolveUiErrorMessage(
        error,
        "Failed to save site settings.",
        "You appear to be offline. Reconnect before saving site settings.",
      ));
    } finally {
      setIsSaving(false);
    }
  };

  const updateNavItem = (itemId: string, patch: Partial<NavItemDraft>) => {
    setNavItems((current) => current.map((item) => (
      item.id === itemId ? { ...item, ...patch } : item
    )));
  };

  const addNavItem = () => {
    setNavItems((current) => [
      ...current,
      {
        id: `nav-${Date.now()}-${current.length + 1}`,
        label: "New link",
        url: "/",
        openInNewTab: false,
      },
    ]);
  };

  const removeNavItem = (itemId: string) => {
    setNavItems((current) => current.filter((item) => item.id !== itemId));
  };

  const handleSaveNavigation = async () => {
    if (!selectedSite) return;

    setIsSavingNavigation(true);
    setNavigationError(null);
    setNavigationMessage(null);

    try {
      const response = await apiFetch<{ data?: SiteNavigationResponse }>(
        `/crm/pages/sites/${selectedSite.id}/nav/${activeNavType}`,
        {
          method: "PUT",
          body: JSON.stringify({
            items: navItems
              .filter((item) => item.label.trim().length > 0)
              .map((item) => ({
                id: item.id,
                label: item.label.trim(),
                ...(item.url.trim() ? { url: item.url.trim() } : {}),
                ...(item.openInNewTab ? { openInNewTab: true } : {}),
              })),
          }),
        },
      );

      setNavItems((response.data?.items ?? []).map((item) => ({
        id: item.id,
        label: item.label,
        url: item.url ?? "",
        openInNewTab: item.openInNewTab ?? false,
      })));
      setNavigationMessage(`${activeNavType} navigation saved. Republish the site for published pages to pick up the new menu.`);
    } catch (error) {
      setNavigationError(resolveUiErrorMessage(
        error,
        "Failed to save site navigation.",
        "You appear to be offline. Reconnect before saving site navigation.",
      ));
    } finally {
      setIsSavingNavigation(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading site settings...
      </div>
    );
  }

  if (loadError && !selectedSite) {
    return (
      <div className="rounded-[28px] border border-destructive/25 bg-destructive/5 p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div className="space-y-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Site Settings</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                The mounted site resource could not be loaded, so this route cannot tell you whether a site exists yet.
              </p>
            </div>
            <div
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className="rounded-2xl border border-destructive/20 bg-background/80 px-4 py-3 text-sm text-destructive"
            >
              {loadError}
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate(0)}
                className={`inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 ${FOCUS_RING_CLASS}`}
              >
                Retry load
              </button>
              <Link
                to="/pages/sites"
                className={`inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent ${FOCUS_RING_CLASS}`}
              >
                Back to pages
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedSite) {
    return (
      <div className="rounded-[28px] border border-border bg-card p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
            <Settings2 className="h-5 w-5" />
          </div>
          <div className="space-y-3">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Site Settings</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                This page edits a mounted site resource. There is no site to edit yet, so create a site or page first and return here.
              </p>
            </div>
            <Link
              to="/pages/sites"
              className={`inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 ${FOCUS_RING_CLASS}`}
            >
              Go to pages
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isPickOneEntry && pickOneForm) {
    return (
      <PickOneCustomizationPage
        sites={sites}
        selectedSiteId={selectedSiteId}
        onSelectSite={setSelectedSiteId}
        selectedSite={selectedSite}
        selectedTemplateName={selectedTemplate?.name ?? null}
        selectedStarterName={selectedStarter?.title ?? null}
        templateVertical={templateVertical}
        form={pickOneForm}
        updateForm={(updater) => setPickOneForm((current) => (current ? updater(current) : current))}
        previewTheme={pickOnePreviewTheme}
        previewStyle={pickOnePreviewStyle}
        isSaving={isSaving}
        loadError={loadError}
        saveMessage={saveMessage}
        onSave={async () => {
          await persistPickOneCustomization();
        }}
        onContinue={async () => {
          const didSave = await persistPickOneCustomization();
          if (!didSave) {
            return;
          }

          const search = new URLSearchParams();
          if (selectedSiteId) {
            search.set("siteId", selectedSiteId);
          }
          if (templateVertical) {
            search.set("template", templateVertical);
          }
          if (starterPageId) {
            search.set("starter", starterPageId);
          }
          navigate(`/pages/ai-generate?${search.toString()}`);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link
          to="/settings/brand"
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-medium transition hover:bg-accent hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Brand Kit
        </Link>
        <span className="text-xs">/</span>
        <span className="font-medium text-foreground">Site Theme</span>
      </nav>

      <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.12),_transparent_40%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-muted-foreground">Mounted Site Resource</p>
            <h1 className="text-2xl font-bold text-foreground">Site-wide themes now persist to the real `/crm/pages/sites/:id` contract.</h1>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              This route now edits a real site theme object with presets, custom controls, and published-page CSS variable output. Global header/footer content uses the same mounted site resource through dedicated shell editor flows.
            </p>
          </div>

          <div className="w-full max-w-xs">
            <label htmlFor="site-selector" className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Active site
            </label>
            <select
              id="site-selector"
              value={selectedSiteId}
              onChange={(event) => setSelectedSiteId(event.target.value)}
              className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground"
            >
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name} ({site.slug})
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {loadError && (
        <div
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {pageLoadError ? (
        <div
          role="alert"
          aria-live="polite"
          aria-atomic="true"
          className={ADVISORY_NOTICE_CLASS}
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{pageLoadError}</span>
        </div>
      ) : null}

      {saveMessage && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={SUCCESS_NOTICE_CLASS}
        >
          {saveMessage}
        </div>
      )}

      <form onSubmit={handleSave} className="grid gap-6 xl:grid-cols-[1.12fr_0.88fr]">
        <div className="space-y-6">
          <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-foreground">General</h2>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label htmlFor="site-name" className="block text-sm font-medium text-foreground">
                  Site name
                </label>
                <input
                  id="site-name"
                  type="text"
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground"
                />
              </div>

              <div>
                <label htmlFor="site-slug" className="block text-sm font-medium text-foreground">
                  Site slug
                </label>
                <input
                  id="site-slug"
                  type="text"
                  value={form.slug}
                  onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))}
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground"
                />
              </div>

              <div>
                <label htmlFor="home-page" className="block text-sm font-medium text-foreground">
                  Home page
                </label>
                <select
                  id="home-page"
                  value={form.homePageId}
                  onChange={(event) => setForm((current) => ({ ...current, homePageId: event.target.value }))}
                  disabled={Boolean(pageLoadError)}
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground"
                >
                  <option value="">{pageLoadError ? "Page inventory unavailable" : "No home page selected"}</option>
                  {pages.map((page) => (
                    <option key={page.id} value={page.id}>
                      {page.title} (/{page.slug})
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="meta-description" className="block text-sm font-medium text-foreground">
                  Default meta description
                </label>
                <textarea
                  id="meta-description"
                  value={form.metaDescription}
                  onChange={(event) => setForm((current) => ({ ...current, metaDescription: event.target.value }))}
                  rows={3}
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground"
                  placeholder="Fallback search description for pages that do not override it."
                />
              </div>
            </div>
          </section>

          <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Global Theme</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Presets and custom tokens save to the shared site theme contract and are consumed by published pages through CSS variables.
                </p>
              </div>
              <Link
                to="/settings/brand"
                className={`inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-accent ${FOCUS_RING_CLASS}`}
              >
                Open brand kit
              </Link>
            </div>

            <div className="mt-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
                <Sparkles className="h-4 w-4 text-primary" />
                Presets
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {SITE_THEME_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setForm((current) => applyPresetToForm(current, preset.theme))}
                    className={`rounded-2xl border p-4 text-left transition ${
                      form.themePresetId === preset.id
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border bg-background hover:border-foreground/20 hover:bg-accent"
                    } ${FOCUS_RING_CLASS}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-foreground">{preset.name}</p>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">{preset.description}</p>
                      </div>
                      {form.themePresetId === preset.id ? <Check className="mt-0.5 h-4 w-4 text-primary" /> : null}
                    </div>
                    <div className="mt-4 flex gap-2">
                      {[
                        preset.theme.colors?.brand,
                        preset.theme.colors?.secondary,
                        preset.theme.colors?.accent,
                        preset.theme.colors?.surface,
                      ].map((color) => (
                        <span
                          key={`${preset.id}-${color}`}
                          className="h-8 w-8 rounded-full border border-black/10"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{preset.theme.fonts?.heading?.family}</span>
                      <span>{preset.theme.buttonStyle} / {preset.theme.cardStyle}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2 rounded-2xl border border-accent/40 bg-accent/10 px-4 py-3 text-sm text-accent-foreground">
                <p className="font-medium">Inheritance model</p>
                <p className="mt-1 leading-6">
                  Fields marked <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-1.5 py-0 text-[10px] font-semibold text-primary">Brand default</span> inherit from your brand kit and will update when you change the brand.
                  Fields marked <span className="inline-flex items-center rounded-full border border-accent/40 bg-accent px-1.5 py-0 text-[10px] font-semibold text-accent-foreground">Site override</span> use a value you set here, ignoring the brand kit for that token.
                  Fields marked <span className="inline-flex items-center rounded-full border border-muted-foreground/30 bg-muted px-1.5 py-0 text-[10px] font-semibold text-muted-foreground">Preset fallback</span> stay owned by the active preset because this site has not overridden them and the brand kit does not define that token.
                  Clear any field to fall back to brand defaults.
                </p>
              </div>
              <div className="md:col-span-2 rounded-2xl border border-border bg-background p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Current inheritance split</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      This tells you how much of the current site theme is still flowing from the shared brand contract versus what this site now owns directly.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="grid gap-2 sm:grid-cols-3">
                      <SummaryChip
                        label={pluralizeInheritanceCount(inheritanceSummary.brandDefaultCount, "brand default")}
                        tone="brand"
                      />
                      <SummaryChip
                        label={pluralizeInheritanceCount(inheritanceSummary.siteOverrideCount, "site override")}
                        tone="override"
                      />
                      <SummaryChip
                        label={pluralizeInheritanceCount(inheritanceSummary.presetFallbackCount, "preset fallback")}
                        tone="preset"
                      />
                    </div>
                    {hasBrandRefreshAction ? (
                      <button
                        type="button"
                        onClick={() => {
                          setForm((current) => clearBrandDerivedOverrides(current));
                          setSaveMessage("Preview refreshed from brand defaults. Save mounted site settings to persist this site-level change.");
                        }}
                        className={`inline-flex items-center justify-center gap-2 self-start rounded-xl border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-accent ${FOCUS_RING_CLASS}`}
                      >
                        <Sparkles className="h-4 w-4 text-primary" />
                        Refresh from brand defaults
                      </button>
                    ) : null}
                  </div>
                </div>
                <p className="mt-4 text-sm leading-6 text-muted-foreground">
                  {inheritanceSummary.siteOverrideCount > 0
                    ? `This site is explicitly overriding ${inheritanceSummary.overriddenFields.join(", ")}.`
                    : "This site is still fully inheriting brand-managed tokens unless a field is changed here."}
                </p>
                {hasBrandRefreshAction ? (
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    Refreshing clears only the brand-derived overrides for this site: {inheritanceSummary.overriddenFields.join(", ")}.
                    Preset-only tokens, custom scripts, SEO fields, and shell settings stay untouched.
                  </p>
                ) : null}
              </div>
              <ThemeInput
                id="theme-brand"
                label="Primary / brand"
                value={form.themeBrand}
                onChange={(value) => setForm((current) => ({ ...current, themeBrand: value }))}
                placeholder={previewTheme.colors?.brand ?? DEFAULT_PREVIEW_BRAND}
                inheritance={resolvedTheme.inheritance["colors.brand"]}
              />
              <ThemeInput
                id="theme-brand-dark"
                label="Primary dark"
                value={form.themeBrandDark}
                onChange={(value) => setForm((current) => ({ ...current, themeBrandDark: value }))}
                placeholder={previewTheme.colors?.brandDark ?? previewTheme.colors?.brand ?? DEFAULT_PREVIEW_BRAND_DARK}
                inheritance={resolvedTheme.inheritance["colors.brandDark"]}
              />
              <ThemeInput
                id="theme-secondary"
                label="Secondary"
                value={form.themeSecondary}
                onChange={(value) => setForm((current) => ({ ...current, themeSecondary: value }))}
                placeholder={previewTheme.colors?.secondary ?? DEFAULT_PREVIEW_SECONDARY}
                inheritance={resolvedTheme.inheritance["colors.secondary"]}
              />
              <ThemeInput
                id="theme-accent"
                label="Accent"
                value={form.themeAccent}
                onChange={(value) => setForm((current) => ({ ...current, themeAccent: value }))}
                placeholder={previewTheme.colors?.accent ?? DEFAULT_PREVIEW_ACCENT}
                inheritance={resolvedTheme.inheritance["colors.accent"]}
              />
              <ThemeInput
                id="theme-background"
                label="Background"
                value={form.themeBackground}
                onChange={(value) => setForm((current) => ({ ...current, themeBackground: value }))}
                placeholder={previewTheme.colors?.background ?? DEFAULT_PREVIEW_BACKGROUND}
                inheritance={resolvedTheme.inheritance["colors.background"]}
              />
              <ThemeInput
                id="theme-surface"
                label="Surface"
                value={form.themeSurface}
                onChange={(value) => setForm((current) => ({ ...current, themeSurface: value }))}
                placeholder={previewTheme.colors?.surface ?? DEFAULT_PREVIEW_SURFACE}
                inheritance={resolvedTheme.inheritance["colors.surface"]}
              />
              <ThemeInput
                id="theme-muted"
                label="Muted"
                value={form.themeMuted}
                onChange={(value) => setForm((current) => ({ ...current, themeMuted: value }))}
                placeholder={previewTheme.colors?.muted ?? DEFAULT_PREVIEW_MUTED}
                inheritance={resolvedTheme.inheritance["colors.muted"]}
              />
              <ThemeInput
                id="theme-text"
                label="Text"
                value={form.themeText}
                onChange={(value) => setForm((current) => ({ ...current, themeText: value }))}
                placeholder={previewTheme.colors?.text ?? DEFAULT_PREVIEW_TEXT}
                inheritance={resolvedTheme.inheritance["colors.text"]}
              />
              <ThemeSelect
                id="theme-heading-font"
                label="Heading font"
                value={form.themeHeadingFont}
                onChange={(value) => setForm((current) => ({ ...current, themeHeadingFont: value }))}
                options={fontOptions}
                fallbackLabel={previewTheme.fonts?.heading?.family ?? "Use preset / brand fallback"}
                inheritance={resolvedTheme.inheritance["fonts.heading"]}
              />
              <ThemeSelect
                id="theme-body-font"
                label="Body font"
                value={form.themeBodyFont}
                onChange={(value) => setForm((current) => ({ ...current, themeBodyFont: value }))}
                options={fontOptions}
                fallbackLabel={previewTheme.fonts?.body?.family ?? "Use preset / brand fallback"}
                inheritance={resolvedTheme.inheritance["fonts.body"]}
              />
              <ThemeInput
                id="theme-radius"
                label="Border radius"
                value={form.themeBorderRadius}
                onChange={(value) => setForm((current) => ({ ...current, themeBorderRadius: value }))}
                placeholder={previewTheme.borderRadius ?? DEFAULT_PREVIEW_RADIUS}
              />
              <ThemeInput
                id="theme-spacing"
                label="Section spacing"
                value={form.themeSpacing}
                onChange={(value) => setForm((current) => ({ ...current, themeSpacing: value }))}
                placeholder={previewTheme.spacing ?? DEFAULT_PREVIEW_SPACING}
              />
              <ThemeEnumSelect
                id="theme-button-style"
                label="Button style"
                value={form.themeButtonStyle}
                onChange={(value) => setForm((current) => ({ ...current, themeButtonStyle: value }))}
                options={SITE_THEME_BUTTON_STYLES}
              />
              <ThemeEnumSelect
                id="theme-card-style"
                label="Card style"
                value={form.themeCardStyle}
                onChange={(value) => setForm((current) => ({ ...current, themeCardStyle: value }))}
                options={SITE_THEME_CARD_STYLES}
              />
            </div>

            <div className="mt-6 rounded-[28px] border border-border bg-background p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Live preview</p>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    Local preview updates immediately from the same theme object that will be persisted to the site resource.
                  </p>
                </div>
                <span className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
                  <Palette className="h-3.5 w-3.5" />
                  {previewTheme.presetId ? `Preset: ${titleCase(previewTheme.presetId)}` : "Custom theme"}
                </span>
              </div>

              <div className="mt-5 overflow-hidden rounded-[24px] border border-black/10" style={previewStyle}>
                <div
                  className="border-b px-5 py-4"
                  style={{
                    borderColor: "var(--preview-border)",
                    background: "var(--preview-surface)",
                  }}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p
                        className="text-xs font-semibold uppercase tracking-[0.24em]"
                        style={{ color: "var(--preview-brand)" }}
                      >
                        Published page
                      </p>
                      <p
                        className="mt-2 text-sm"
                        style={{ color: previewTheme.colors?.text ?? DEFAULT_PREVIEW_TEXT, opacity: 0.75 }}
                      >
                        {selectedSite.slug}.stero.ai
                      </p>
                    </div>
                    <button
                      type="button"
                      className="rounded-full px-4 py-2 text-sm font-semibold"
                      style={buildPreviewButtonStyle(previewTheme, false)}
                    >
                      Contact
                    </button>
                  </div>
                </div>

                <div className="px-5 py-8" style={{ background: "var(--preview-bg)" }}>
                  <div className="max-w-xl space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--preview-accent)" }}>
                      Site-wide theme
                    </p>
                    <h3
                      className="text-3xl font-semibold"
                      style={{ fontFamily: buildPreviewFont(previewTheme.fonts?.heading?.family) }}
                    >
                      Theme swaps now affect the whole published site.
                    </h3>
                    <p
                      className="max-w-lg text-sm leading-6"
                      style={{
                        color: previewTheme.colors?.text ?? DEFAULT_PREVIEW_TEXT,
                        opacity: 0.78,
                        fontFamily: buildPreviewFont(previewTheme.fonts?.body?.family),
                      }}
                    >
                      Presets set the global tone, custom edits refine it, and the renderer consumes the result through CSS variables instead of a fake editor-only state.
                    </p>
                    <div className="flex flex-wrap gap-3 pt-2">
                      <button
                        type="button"
                        className="rounded-full px-5 py-3 text-sm font-semibold"
                        style={buildPreviewButtonStyle(previewTheme, true)}
                      >
                        Primary CTA
                      </button>
                      <button
                        type="button"
                        className="rounded-full px-5 py-3 text-sm font-semibold"
                        style={buildPreviewButtonStyle(previewTheme, false)}
                      >
                        Secondary CTA
                      </button>
                    </div>
                  </div>

                  <div className="mt-8 grid gap-4 md:grid-cols-3">
                    {[
                      {
                        title: "Header + footer",
                        body: "Navigation chrome inherits surface, border, and typography tokens.",
                      },
                      {
                        title: "Buttons",
                        body: `Current style: ${previewTheme.buttonStyle ?? "default"}. Primary and secondary states stay site-wide.`,
                      },
                      {
                        title: "Cards",
                        body: `Current style: ${previewTheme.cardStyle ?? "default"}. Shared surface + shadow tokens apply consistently.`,
                      },
                    ].map((card) => (
                      <div
                        key={card.title}
                        className="px-4 py-4"
                        style={buildPreviewCardStyle(previewTheme)}
                      >
                        <p
                          className="text-base font-semibold"
                          style={{
                            color: "var(--preview-text)",
                            fontFamily: buildPreviewFont(previewTheme.fonts?.heading?.family),
                          }}
                        >
                          {card.title}
                        </p>
                        <p
                          className="mt-2 text-sm leading-6"
                          style={{
                            color: previewTheme.colors?.text ?? DEFAULT_PREVIEW_TEXT,
                            opacity: 0.74,
                            fontFamily: buildPreviewFont(previewTheme.fonts?.body?.family),
                          }}
                        >
                          {card.body}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {brand ? (
                <div className="mt-4 space-y-1 text-sm leading-6 text-muted-foreground">
                  <p>Brand kit active: {formatBrandSummary(brand)}</p>
                  {resolvedTheme.hasAnyBrandInheritance ? (
                    <p>
                      Some theme tokens are inherited from the brand kit. Clear a site override to let the brand default flow through.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </section>

          <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-foreground">Publishing Scripts</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              These fields map directly to the mounted site resource and are injected into published site output.
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label htmlFor="head-scripts" className="block text-sm font-medium text-foreground">
                  Head scripts
                </label>
                <textarea
                  id="head-scripts"
                  value={form.headScripts}
                  onChange={(event) => setForm((current) => ({ ...current, headScripts: event.target.value }))}
                  rows={5}
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 font-mono text-sm text-foreground"
                  placeholder="<!-- Scripts injected into <head> -->"
                />
              </div>

              <div>
                <label htmlFor="body-end-scripts" className="block text-sm font-medium text-foreground">
                  Body-end scripts
                </label>
                <textarea
                  id="body-end-scripts"
                  value={form.bodyEndScripts}
                  onChange={(event) => setForm((current) => ({ ...current, bodyEndScripts: event.target.value }))}
                  rows={5}
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 font-mono text-sm text-foreground"
                  placeholder="<!-- Scripts injected before </body> -->"
                />
              </div>
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-foreground">Search Controls</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              `robots.txt` is mounted. Favicon and broader SEO asset management are not edited from this route yet.
            </p>

            <div className="mt-5">
              <label htmlFor="robots-txt" className="block text-sm font-medium text-foreground">
                robots.txt
              </label>
              <textarea
                id="robots-txt"
                value={form.robotsTxt}
                onChange={(event) => setForm((current) => ({ ...current, robotsTxt: event.target.value }))}
                rows={8}
                className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 font-mono text-sm text-foreground"
                placeholder={"User-agent: *\nAllow: /"}
              />
            </div>
          </section>

          <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-foreground">Global site shell</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Header and footer are real site-level Puck documents. Navigation is mounted on the site navigation endpoints.
              Saving any shell control should be followed by a site republish so published pages render the same shell.
            </p>

            <div className="mt-5 space-y-4">
              <ShellStatusCard
                title="Global header"
                description={describeShellContent(selectedSite.headerContent, "Header")}
                href={`/pages/editor/new?siteId=${selectedSite.id}&shell=header`}
                actionLabel="Edit global header"
              />
              <ShellStatusCard
                title="Global footer"
                description={describeShellContent(selectedSite.footerContent, "Footer")}
                href={`/pages/editor/new?siteId=${selectedSite.id}&shell=footer`}
                actionLabel="Edit global footer"
              />
              <ShellStatusCard
                title="Site navigation"
                description="Edit ordered main, footer, and mobile menu links from the mounted site navigation contract."
                href={`/pages/settings?siteId=${selectedSite.id}&panel=navigation`}
                actionLabel="Edit navigation"
              />
            </div>
          </section>

          <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                  <Menu className="h-4 w-4 text-primary" />
                  Navigation
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Main, footer, and mobile menus save to `/crm/pages/sites/:siteId/nav/:type`.
                  Row order is persisted as the menu order.
                </p>
              </div>
              {activePanel !== "navigation" ? (
                <Link
                  to={`/pages/settings?siteId=${selectedSite.id}&panel=navigation`}
                  className={`inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent ${FOCUS_RING_CLASS}`}
                >
                  Open navigation editor
                </Link>
              ) : null}
            </div>

            {activePanel === "navigation" ? (
              <div className="mt-5 space-y-4">
                <div className="flex flex-wrap gap-2">
                  {NAV_TYPES.map((navType) => (
                    <button
                      key={navType}
                      type="button"
                      onClick={() => setActiveNavType(navType)}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] transition ${
                        activeNavType === navType
                          ? "bg-primary text-primary-foreground"
                          : "border border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                      }`}
                    >
                      {navType}
                    </button>
                  ))}
                </div>

                {navigationError ? (
                  <div role="alert" aria-live="polite" className={ADVISORY_NOTICE_CLASS}>
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{navigationError}</span>
                  </div>
                ) : null}

                {navigationMessage ? (
                  <div className={SUCCESS_NOTICE_CLASS}>
                    {navigationMessage}
                  </div>
                ) : null}

                {isLoadingNavigation ? (
                  <div className="flex items-center gap-2 rounded-2xl border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading {activeNavType} navigation...
                  </div>
                ) : (
                  <div className="space-y-3">
                    {navItems.map((item, index) => (
                      <div key={item.id} className="rounded-2xl border border-border bg-background p-4">
                        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                          <label className="space-y-1 text-sm">
                            <span className="font-medium text-foreground">Label</span>
                            <input
                              value={item.label}
                              onChange={(event) => updateNavItem(item.id, { label: event.target.value })}
                              className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                              placeholder={`Navigation item ${index + 1}`}
                            />
                          </label>
                          <label className="space-y-1 text-sm">
                            <span className="font-medium text-foreground">URL</span>
                            <input
                              value={item.url}
                              onChange={(event) => updateNavItem(item.id, { url: event.target.value })}
                              className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                              placeholder="/contact"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => removeNavItem(item.id)}
                            className={`self-end rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground ${FOCUS_RING_CLASS}`}
                          >
                            Remove
                          </button>
                        </div>
                        <label className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={item.openInNewTab}
                            onChange={(event) => updateNavItem(item.id, { openInNewTab: event.target.checked })}
                            className="h-4 w-4 rounded border-input"
                          />
                          Open in new tab
                        </label>
                      </div>
                    ))}

                    {navItems.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-border bg-background px-4 py-6 text-sm text-muted-foreground">
                        No {activeNavType} links saved yet. Add a link to create this menu.
                      </div>
                    ) : null}
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={addNavItem}
                    className={`inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent ${FOCUS_RING_CLASS}`}
                  >
                    Add link
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSaveNavigation()}
                    disabled={isSavingNavigation || isLoadingNavigation}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
                  >
                    {isSavingNavigation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save navigation
                  </button>
                </div>
              </div>
            ) : null}
          </section>

          <section className={ADVISORY_SECTION_CLASS}>
            <h2 className="text-lg font-semibold text-foreground">Explicitly unsupported here</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              These controls still need dedicated mounted routes. They remain listed here to keep the scope boundaries explicit.
            </p>

            <ul className="mt-5 space-y-3">
              {UNSUPPORTED_SURFACES.map((item) => (
                <li key={item} className={ADVISORY_ITEM_CLASS}>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save mounted site settings
          </button>
        </div>
      </form>
    </div>
  );
}

interface PickOneCustomizationPageProps {
  sites: SiteRecord[];
  selectedSiteId: string;
  onSelectSite: (siteId: string) => void;
  selectedSite: SiteRecord;
  selectedTemplateName: string | null;
  selectedStarterName: string | null;
  templateVertical: string | null;
  form: PickOneFormState;
  updateForm: (updater: (current: PickOneFormState) => PickOneFormState) => void;
  previewTheme: SiteTheme;
  previewStyle: CSSProperties;
  isSaving: boolean;
  loadError: string | null;
  saveMessage: string | null;
  onSave: () => Promise<void>;
  onContinue: () => Promise<void>;
}

function PickOneCustomizationPage({
  sites,
  selectedSiteId,
  onSelectSite,
  selectedSite,
  selectedTemplateName,
  selectedStarterName,
  templateVertical,
  form,
  updateForm,
  previewTheme,
  previewStyle,
  isSaving,
  loadError,
  saveMessage,
  onSave,
  onContinue,
}: PickOneCustomizationPageProps) {
  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.12),_transparent_40%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-muted-foreground">Pick One Customization</p>
            <h1 className="text-2xl font-bold text-foreground">Make the starter feel like your business before the draft is generated.</h1>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              This step keeps the scope intentionally small: logo, colors, font mood, and business basics. Save the real brand and site-theme contracts, then continue into AI draft generation.
            </p>
          </div>

          <div className="w-full max-w-sm rounded-2xl border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">{selectedTemplateName ?? "Starter selected"}</p>
            <p className="mt-1">{selectedStarterName ? `${selectedStarterName} page` : "Starter page selected"}</p>
            {templateVertical ? <p className="mt-1 uppercase tracking-[0.2em] text-xs">{templateVertical}</p> : null}
          </div>
        </div>
      </section>

      {loadError ? (
        <div
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{loadError}</span>
        </div>
      ) : null}

      {saveMessage ? (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={SUCCESS_NOTICE_CLASS}
        >
          {saveMessage}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[1.02fr_0.98fr]">
        <section className="space-y-6 rounded-[24px] border border-border bg-card p-6 shadow-sm">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Business basics</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              These fields write into the mounted site and brand records. No fake draft state.
            </p>
          </div>

          <div className="grid gap-4">
            <div>
              <label htmlFor="pick-one-site" className="block text-sm font-medium text-foreground">
                Website
              </label>
              <select
                id="pick-one-site"
                value={selectedSiteId}
                onChange={(event) => onSelectSite(event.target.value)}
                className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground"
              >
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name} ({site.slug})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="pick-one-site-name" className="block text-sm font-medium text-foreground">
                Website name
              </label>
              <input
                id="pick-one-site-name"
                value={form.siteName}
                onChange={(event) => updateForm((current) => ({ ...current, siteName: event.target.value }))}
                className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground"
              />
            </div>

            <div>
              <label htmlFor="pick-one-business-name" className="block text-sm font-medium text-foreground">
                Business name
              </label>
              <input
                id="pick-one-business-name"
                value={form.businessName}
                onChange={(event) => updateForm((current) => ({ ...current, businessName: event.target.value }))}
                className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground"
              />
            </div>

            <div>
              <label htmlFor="pick-one-tagline" className="block text-sm font-medium text-foreground">
                Tagline
              </label>
              <input
                id="pick-one-tagline"
                value={form.tagline}
                onChange={(event) => updateForm((current) => ({ ...current, tagline: event.target.value }))}
                placeholder="What should people remember in one short line?"
                className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground"
              />
            </div>

            <div>
              <label htmlFor="pick-one-logo" className="block text-sm font-medium text-foreground">
                Logo URL
              </label>
              <input
                id="pick-one-logo"
                value={form.logoUrl}
                onChange={(event) => updateForm((current) => ({ ...current, logoUrl: event.target.value }))}
                placeholder="https://..."
                className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground"
              />
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground">Look and feel</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {templateVertical
                ? "Keep the choices curated. This vertical starter locks one approved font pair by default, and you only tune the core brand colors here."
                : "Keep the choices curated. Pick a font mood, then tune the four colors that show up immediately in preview."}
            </p>

            {templateVertical ? (
              <div className="mt-5 rounded-2xl border border-border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                Font pairing is auto-selected from the <span className="font-medium text-foreground">{templateVertical}</span> vertical starter.
                Use the advanced site theme editor later if you need to override it.
              </div>
            ) : (
              <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {FONT_MOOD_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => updateForm((current) => ({ ...current, fontMood: option.id }))}
                    className={`rounded-2xl border p-4 text-left transition ${
                      form.fontMood === option.id
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border bg-background hover:border-foreground/20 hover:bg-accent"
                    } ${FOCUS_RING_CLASS}`}
                  >
                    <p className="font-medium text-foreground">{option.label}</p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">{option.description}</p>
                  </button>
                ))}
              </div>
            )}

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <ThemeInput
                id="pick-one-primary"
                label="Primary color"
                value={form.primaryColor}
                onChange={(value) => updateForm((current) => ({ ...current, primaryColor: value }))}
                placeholder={DEFAULT_PREVIEW_BRAND}
              />
              <ThemeInput
                id="pick-one-accent"
                label="Accent color"
                value={form.accentColor}
                onChange={(value) => updateForm((current) => ({ ...current, accentColor: value }))}
                placeholder={DEFAULT_PREVIEW_ACCENT}
              />
              <ThemeInput
                id="pick-one-background"
                label="Background color"
                value={form.backgroundColor}
                onChange={(value) => updateForm((current) => ({ ...current, backgroundColor: value }))}
                placeholder={DEFAULT_PREVIEW_BACKGROUND}
              />
              <ThemeInput
                id="pick-one-text"
                label="Text color"
                value={form.textColor}
                onChange={(value) => updateForm((current) => ({ ...current, textColor: value }))}
                placeholder={DEFAULT_PREVIEW_TEXT}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={isSaving}
              className={`inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-50 ${FOCUS_RING_CLASS}`}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save basics
            </button>
            <button
              type="button"
              onClick={() => void onContinue()}
              disabled={isSaving || !form.siteName.trim() || !form.businessName.trim()}
              className={`inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50 ${FOCUS_RING_CLASS}`}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Continue to draft
            </button>
          </div>
        </section>

        <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Live preview</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Changes here update immediately from the same theme object that will be saved to the site.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
              <Palette className="h-3.5 w-3.5" />
              {selectedSite.slug}.stero.ai
            </span>
          </div>

          <div className="mt-5 overflow-hidden rounded-[24px] border border-black/10" style={previewStyle}>
            <div
              className="border-b px-5 py-4"
              style={{
                borderColor: "var(--preview-border)",
                background: "var(--preview-surface)",
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {form.logoUrl ? (
                    <img src={form.logoUrl} alt="" className="h-10 w-10 rounded-xl object-cover" />
                  ) : (
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-xl text-xs font-semibold"
                      style={{
                        background: previewTheme.colors?.brand ?? DEFAULT_PREVIEW_BRAND,
                        color: previewTheme.colors?.background ?? DEFAULT_PREVIEW_BACKGROUND,
                      }}
                    >
                      {form.businessName.trim().charAt(0).toUpperCase() || "B"}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-foreground">{form.businessName || form.siteName}</p>
                    <p className="text-xs text-muted-foreground">{form.tagline || "A clear starter with your brand basics in place."}</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded-full px-4 py-2 text-sm font-semibold"
                  style={buildPreviewButtonStyle(previewTheme, false)}
                >
                  Contact
                </button>
              </div>
            </div>

            <div className="px-5 py-8" style={{ background: "var(--preview-bg)" }}>
              <div className="max-w-xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--preview-accent)" }}>
                  {selectedTemplateName ?? "Starter"} preview
                </p>
                <h3
                  className="text-3xl font-semibold"
                  style={{ fontFamily: buildPreviewFont(previewTheme.fonts?.heading?.family) }}
                >
                  {form.tagline || `A calmer first draft for ${form.businessName || form.siteName}.`}
                </h3>
                <p
                  className="max-w-lg text-sm leading-6"
                  style={{
                    color: previewTheme.colors?.text ?? DEFAULT_PREVIEW_TEXT,
                    opacity: 0.78,
                    fontFamily: buildPreviewFont(previewTheme.fonts?.body?.family),
                  }}
                >
                  This preview is intentionally simple. It shows the brand basics that will shape the template-first draft before anyone touches the full editor.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    type="button"
                    className="rounded-full px-5 py-3 text-sm font-semibold"
                    style={buildPreviewButtonStyle(previewTheme, true)}
                  >
                    Book a call
                  </button>
                  <button
                    type="button"
                    className="rounded-full px-5 py-3 text-sm font-semibold"
                    style={buildPreviewButtonStyle(previewTheme, false)}
                  >
                    Learn more
                  </button>
                </div>
              </div>

              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {[
                  {
                    title: "Brand first",
                    body: "Logo, business name, and tagline are saved before the draft exists.",
                  },
                  {
                    title: "Font mood",
                    body: `Current mood: ${FONT_MOOD_OPTIONS.find((option) => option.id === form.fontMood)?.label ?? "Clean"}.`,
                  },
                  {
                    title: "Site theme",
                    body: "The same global theme contract will carry this into published pages later.",
                  },
                ].map((card) => (
                  <div
                    key={card.title}
                    className="px-4 py-4"
                    style={buildPreviewCardStyle(previewTheme)}
                  >
                    <p
                      className="text-base font-semibold"
                      style={{
                        color: "var(--preview-text)",
                        fontFamily: buildPreviewFont(previewTheme.fonts?.heading?.family),
                      }}
                    >
                      {card.title}
                    </p>
                    <p
                      className="mt-2 text-sm leading-6"
                      style={{
                        color: previewTheme.colors?.text ?? DEFAULT_PREVIEW_TEXT,
                        opacity: 0.74,
                        fontFamily: buildPreviewFont(previewTheme.fonts?.body?.family),
                      }}
                    >
                      {card.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function readMetaDescription(seoDefaults: Record<string, unknown>): string {
  const description = seoDefaults.description;
  return typeof description === "string" ? description : "";
}

function normalizeNullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isOfflineBrowser(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function resolveUiErrorMessage(
  error: unknown,
  fallback: string,
  offlineFallback: string,
): string {
  if (isOfflineBrowser()) {
    return offlineFallback;
  }
  if (error instanceof ApiError) {
    return error.message;
  }
  return fallback;
}

function buildThemePayload(form: SiteFormState): SiteTheme {
  return sanitizeSiteTheme({
    presetId: form.themePresetId || undefined,
    colors: {
      brand: form.themeBrand,
      brandDark: form.themeBrandDark,
      secondary: form.themeSecondary,
      accent: form.themeAccent,
      background: form.themeBackground,
      surface: form.themeSurface,
      muted: form.themeMuted,
      text: form.themeText,
    },
    fonts: {
      heading: form.themeHeadingFont.trim()
        ? { family: form.themeHeadingFont.trim(), googleFont: form.themeHeadingFont.trim() }
        : undefined,
      body: form.themeBodyFont.trim()
        ? { family: form.themeBodyFont.trim(), googleFont: form.themeBodyFont.trim() }
        : undefined,
    },
    borderRadius: form.themeBorderRadius,
    spacing: form.themeSpacing,
    buttonStyle: form.themeButtonStyle || undefined,
    cardStyle: form.themeCardStyle || undefined,
  });
}

function buildPreviewTheme(theme: SiteTheme, brand: BrandKitSummary | null): SiteTheme {
  return buildThemePreviewFromBrand(theme, brand);
}

function applyPresetToForm(form: SiteFormState, theme: SiteTheme): SiteFormState {
  const normalized = normalizeSiteTheme(theme);
  return {
    ...form,
    themePresetId: normalized.presetId ?? "",
  };
}

function clearBrandDerivedOverrides(form: SiteFormState): SiteFormState {
  return {
    ...form,
    themeBrand: "",
    themeSecondary: "",
    themeAccent: "",
    themeBackground: "",
    themeText: "",
    themeHeadingFont: "",
    themeBodyFont: "",
  };
}

function formatBrandSummary(brand: BrandKitSummary) {
  const parts = [
    brand.colors.primary ? `primary ${brand.colors.primary}` : null,
    brand.fonts.headings?.family ? `heading font ${brand.fonts.headings.family}` : null,
    brand.brandVoice ? `voice ${brand.brandVoice}` : null,
    brand.tagline ? `tagline "${brand.tagline}"` : null,
  ].filter(Boolean);

  return parts.join(", ");
}

function buildBrandSeedFromTheme(theme: SiteTheme): Pick<BrandKitSummary, "colors" | "fonts"> {
  return {
    colors: {
      ...(theme.colors?.brand ? { primary: theme.colors.brand } : {}),
      ...(theme.colors?.secondary ? { secondary: theme.colors.secondary } : {}),
      ...(theme.colors?.accent ? { accent: theme.colors.accent } : {}),
      ...(theme.colors?.background ? { background: theme.colors.background } : {}),
      ...(theme.colors?.text ? { text: theme.colors.text } : {}),
    },
    fonts: {
      ...(theme.fonts?.heading?.family
        ? {
            headings: {
              family: theme.fonts.heading.family,
              ...(theme.fonts.heading.weight ? { weight: theme.fonts.heading.weight } : {}),
            },
          }
        : {}),
      ...(theme.fonts?.body?.family
        ? {
            body: {
              family: theme.fonts.body.family,
              ...(theme.fonts.body.weight ? { weight: theme.fonts.body.weight } : {}),
            },
          }
        : {}),
    },
  };
}

function titleCase(value: string): string {
  return value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function resolvePreviewTheme(theme: SiteTheme): SiteTheme {
  return normalizeSiteTheme({
    presetId: theme.presetId ?? DEFAULT_PREVIEW_THEME.presetId ?? "modern",
    ...theme,
  });
}

function buildPreviewStyle(theme: SiteTheme): CSSProperties {
  const resolvedTheme = resolvePreviewTheme(theme);
  return {
    "--preview-brand": resolvedTheme.colors?.brand ?? DEFAULT_PREVIEW_BRAND,
    "--preview-brand-dark": resolvedTheme.colors?.brandDark ?? resolvedTheme.colors?.brand ?? DEFAULT_PREVIEW_BRAND_DARK,
    "--preview-secondary": resolvedTheme.colors?.secondary ?? DEFAULT_PREVIEW_SECONDARY,
    "--preview-accent": resolvedTheme.colors?.accent ?? DEFAULT_PREVIEW_ACCENT,
    "--preview-bg": resolvedTheme.colors?.background ?? DEFAULT_PREVIEW_BACKGROUND,
    "--preview-surface": resolvedTheme.colors?.surface ?? DEFAULT_PREVIEW_SURFACE,
    "--preview-muted": resolvedTheme.colors?.muted ?? DEFAULT_PREVIEW_MUTED,
    "--preview-text": resolvedTheme.colors?.text ?? DEFAULT_PREVIEW_TEXT,
    "--preview-border": resolvedTheme.cardStyle === "outline" ? (resolvedTheme.colors?.muted ?? DEFAULT_PREVIEW_MUTED) : "rgba(15,23,42,0.08)",
    "--preview-radius": resolvedTheme.borderRadius ?? DEFAULT_PREVIEW_RADIUS,
  } as CSSProperties;
}

function buildPreviewButtonStyle(theme: SiteTheme, isPrimary: boolean): CSSProperties {
  const resolvedTheme = resolvePreviewTheme(theme);
  const radius = resolvedTheme.borderRadius ?? "9999px";
  const brand = resolvedTheme.colors?.brand ?? DEFAULT_PREVIEW_BRAND;
  const surface = resolvedTheme.colors?.surface ?? DEFAULT_PREVIEW_SURFACE;
  const text = resolvedTheme.colors?.text ?? DEFAULT_PREVIEW_TEXT;
  const muted = resolvedTheme.colors?.muted ?? DEFAULT_PREVIEW_MUTED;

  if (!isPrimary) {
    return {
      borderRadius: radius,
      border: `1px solid ${muted}`,
      background: surface,
      color: text,
      boxShadow: "none",
    };
  }

  switch (resolvedTheme.buttonStyle) {
    case "outline":
      return {
        borderRadius: radius,
        border: `1px solid ${brand}`,
        background: "transparent",
        color: brand,
        boxShadow: "none",
      };
    case "ghost":
      return {
        borderRadius: radius,
        border: "1px solid transparent",
        background: "transparent",
        color: brand,
        boxShadow: "none",
      };
    case "soft":
      return {
        borderRadius: radius,
        border: `1px solid ${muted}`,
        background: surface,
        color: brand,
        boxShadow: "none",
      };
    default:
      return {
        borderRadius: radius,
        border: "1px solid transparent",
        background: brand,
        color: "#FFFFFF",
        boxShadow: "0 12px 24px rgba(15,23,42,0.12)",
      };
  }
}

function buildPreviewCardStyle(theme: SiteTheme): CSSProperties {
  const resolvedTheme = resolvePreviewTheme(theme);
  const surface = resolvedTheme.colors?.surface ?? DEFAULT_PREVIEW_SURFACE;
  const muted = resolvedTheme.colors?.muted ?? DEFAULT_PREVIEW_MUTED;
  const radius = resolvedTheme.borderRadius ?? DEFAULT_PREVIEW_RADIUS;

  switch (resolvedTheme.cardStyle) {
    case "outline":
      return {
        borderRadius: radius,
        background: surface,
        border: `1px solid ${muted}`,
        boxShadow: "none",
      };
    case "shadow":
      return {
        borderRadius: radius,
        background: surface,
        border: "1px solid transparent",
        boxShadow: "0 14px 30px rgba(15,23,42,0.10)",
      };
    case "elevated":
      return {
        borderRadius: radius,
        background: surface,
        border: "1px solid transparent",
        boxShadow: "0 20px 40px rgba(15,23,42,0.18)",
      };
    default:
      return {
        borderRadius: radius,
        background: surface,
        border: "1px solid transparent",
        boxShadow: "none",
      };
  }
}

function buildPreviewFont(fontFamily: string | undefined): string {
  return fontFamily ? `'${fontFamily}', sans-serif` : "system-ui, sans-serif";
}

function describeShellContent(value: Record<string, unknown> | null, label: string) {
  if (!value) {
    return `${label} is empty. Open the shell editor to create the shared site frame.`;
  }

  const content = value.content;
  if (!Array.isArray(content) || content.length === 0) {
    return `${label} is empty. Open the shell editor to create the shared site frame.`;
  }

  return `${label} has ${content.length} block${content.length === 1 ? "" : "s"} in the mounted shell document.`;
}

function InheritanceBadge({ inheritance }: { inheritance?: ThemeInheritanceMap[keyof ThemeInheritanceMap] }) {
  if (!inheritance) {
    return null;
  }

  switch (inheritance.source) {
    case "brand":
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
          Brand default{inheritance.brandValue ? ` (${inheritance.brandValue})` : ""}
        </span>
      );
    case "override":
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-foreground">
          Site override
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-muted-foreground/30 bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Preset fallback
        </span>
      );
  }
}

function pluralizeInheritanceCount(count: number, label: string) {
  return `${count} ${label}${count === 1 ? "" : "s"}`;
}

function SummaryChip({ label, tone }: { label: string; tone: "brand" | "override" | "preset" }) {
  const toneClassName = tone === "brand"
    ? "border-primary/20 bg-primary/10 text-primary"
    : tone === "override"
      ? "border-accent/40 bg-accent text-accent-foreground"
      : "border-muted-foreground/30 bg-muted text-muted-foreground";

  return (
    <div className={`rounded-2xl border px-3 py-2 text-center text-xs font-semibold uppercase tracking-[0.18em] ${toneClassName}`}>
      {label}
    </div>
  );
}

interface ThemeInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  inheritance?: ThemeInheritanceMap[keyof ThemeInheritanceMap];
}

function ThemeInput({ id, label, value, onChange, placeholder, inheritance }: ThemeInputProps) {
  const resetAction = getThemeResetAction(label, inheritance);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <label htmlFor={id} className="block text-sm font-medium text-foreground">
          {label}
        </label>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {value ? (
            <button
              type="button"
              onClick={() => onChange("")}
              aria-label={resetAction.ariaLabel}
              className={RESET_CHIP_CLASS}
            >
              {resetAction.label}
            </button>
          ) : null}
          <InheritanceBadge inheritance={inheritance} />
        </div>
      </div>
      <div className="mt-1 flex items-center gap-3">
        <input
          id={id}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className={`w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground ${FOCUS_RING_CLASS}`}
        />
        {value ? (
          <span
            className="h-10 w-10 shrink-0 rounded-xl border border-black/10"
            style={{ backgroundColor: value }}
            aria-hidden="true"
          />
        ) : null}
      </div>
    </div>
  );
}

interface ThemeSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  fallbackLabel: string;
  inheritance?: ThemeInheritanceMap[keyof ThemeInheritanceMap];
}

function ThemeSelect({ id, label, value, onChange, options, fallbackLabel, inheritance }: ThemeSelectProps) {
  const resetAction = getThemeResetAction(label, inheritance);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <label htmlFor={id} className="block text-sm font-medium text-foreground">
          {label}
        </label>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {value ? (
            <button
              type="button"
              onClick={() => onChange("")}
              aria-label={resetAction.ariaLabel}
              className={RESET_CHIP_CLASS}
            >
              {resetAction.label}
            </button>
          ) : null}
          <InheritanceBadge inheritance={inheritance} />
        </div>
      </div>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground ${FOCUS_RING_CLASS}`}
      >
        <option value="">{`Inherit (${fallbackLabel})`}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

interface ThemeEnumSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
}

function ThemeEnumSelect({ id, label, value, onChange, options }: ThemeEnumSelectProps) {
  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <label htmlFor={id} className="block text-sm font-medium text-foreground">
          {label}
        </label>
        {value ? (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label={`Use preset default for ${label}`}
            className={RESET_CHIP_CLASS}
          >
            Use preset
          </button>
        ) : null}
      </div>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground ${FOCUS_RING_CLASS}`}
      >
        <option value="">Use preset default</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {titleCase(option)}
          </option>
        ))}
      </select>
    </div>
  );
}

function getThemeResetAction(
  label: string,
  inheritance?: ThemeInheritanceMap[keyof ThemeInheritanceMap],
) {
  if (inheritance?.brandValue) {
    return {
      label: "Use inherited",
      ariaLabel: `Use inherited value for ${label}`,
    };
  }

  return {
    label: "Use preset",
    ariaLabel: `Use preset value for ${label}`,
  };
}

interface ShellStatusCardProps {
  title: string;
  description: string;
  href: string;
  actionLabel: string;
}

function ShellStatusCard({ title, description, href, actionLabel }: ShellStatusCardProps) {
  return (
    <div className="rounded-2xl border border-border bg-background p-4">
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
      <Link
        to={href}
        className={`mt-4 inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent ${FOCUS_RING_CLASS}`}
      >
        {actionLabel}
      </Link>
    </div>
  );
}
